/**
 * Event Response Style presets - describes how AI should react to donations, subs, bits, raids
 */
export const EVENT_RESPONSE_STYLE_PRESETS = {
    witty: 'React like a stand-up comedian riffing with the crowd - genuine thanks wrapped in quick wit, puns, or playful jabs. Go for the laugh. CRITICAL: Never use meta-commentary words like "playful", "teasing", "witty", "joking", "kidding", or "just messing with you". Never announce or explain your humor style. Just deliver the joke directly.',
    thankful: 'React with genuine warmth that makes each supporter feel like family. Be heartfelt and personal, not generic. CRITICAL: Never describe your own tone, never say things like "I\'m being sincere" or announce what kind of response you are giving. Just BE warm.',
    hype: 'React like a sports commentator when the home team scores - MAXIMUM energy, celebrate every supporter like they just won the championship. CRITICAL: Never announce that you are being hyped or excited. Never say "I\'m so hyped right now". Just BE hyped through your words.',
    roast: 'Roast supporters like you are their best friend at a comedy roast - tease hard but with obvious affection. The meaner the joke, the more love behind it. CRITICAL: Never use words like "playful", "teasing", "joking", "just kidding", or "all in good fun". Never explain that you are roasting them. Just deliver the roast directly.',
    custom: '', // Uses eventResponseStyleCustom field
};
export const DEFAULT_PERSONA = {
    name: 'Buddy',
    voiceName: 'Orus',
    voiceStyle: 'casual, calm, and conversational',
    voiceAccent: 'Scottish accent',
    personality: 'Quick-witted co-host with a bit of dry humor. Helpful but sharp when riffing.',
    voiceGuidance: '',
    chattiness: 'minimal',
    responseLength: 'brief',
    respondToName: true,
    respondToQuestions: true,
    visionInterjectionEnabled: true,
    customInstructions: '',
    eventResponseStyle: 'witty',
    eventResponseStyleCustom: '',
    avoidTopics: ['religion', 'politics', 'racial topics'],
    streamerName: 'streamer',
    additionalContext: '',
};
export const DEFAULT_TRIGGERS = {
    donations: {
        enabled: true,
        minimumBits: 0,
        readUsername: true,
        readAmount: true,
        maxMessageLength: 300,
    },
    chatAlerts: {
        enabled: true,
        velocityThreshold: 5,
        velocitySustainedSeconds: 3,
        similarityThreshold: 0.7,
        minimumClusterSize: 5,
        ignoreEmoteOnlyMessages: true,
        ignoreShortMessages: true,
        ignoredPhrases: ['gg', 'ez', 'lol', 'lmao', 'pog', 'poggers', 'f', 'w', 'l', 'kek', 'kekw', 'lulw'],
        urgentKeywords: ['look', 'behind', 'watch out', 'danger', 'no', 'yes', 'do it', "don't"],
    },
    cooldowns: {
        afterDonation: 5,
        afterChatAlert: 30,
        afterAnyResponse: 2,
    },
    channelPoints: {
        enabled: false,
        rewardIds: [],
    },
    commands: {
        enabled: true,
        prefix: '!buddy',
        allowedCommands: ['mute', 'unmute', 'status'],
        modOnly: true,
        allowViewerMessages: true,
        cooldown: 10, // 10 second cooldown for viewer messages
        sendCooldownMessages: true, // Send cooldown warnings to chat by default
    },
};
export const DEFAULT_AUDIO = {
    pushToTalk: {
        enabled: true,
        hotkey: 'Backquote',
        hotkeyDisplay: '`',
    },
    deviceId: '', // Empty = system default microphone
    inputGain: 1.0, // 1.0 = normal, 0.5 = half, 2.0 = 2x boost
    outputVolume: 1.0, // 1.0 = normal, 0.0 = mute, 2.0 = 2x boost
    visualsEnabled: true, // Show overlay visuals by default
    overlayScale: 2.0, // Default to 2x scale for crispness
    onDemandVision: true, // Only send frames on interaction by default
};
/** Enforce product safety modes while preserving user-selectable audio fields. */
export function normalizeAudioConfig(config) {
    return {
        ...DEFAULT_AUDIO,
        ...config,
        pushToTalk: {
            ...DEFAULT_AUDIO.pushToTalk,
            ...config?.pushToTalk,
            enabled: true,
        },
        onDemandVision: true,
    };
}
export const DEFAULT_STREAMLABS = {
    enabled: false,
    socketToken: '',
    pauseDuringAlerts: true,
};
export const DEFAULT_PROFANITY_FILTER = {
    enabled: true,
    useDefaultList: true,
    customWords: [],
    whitelistedWords: [],
};
export const DEFAULT_VIEWER_MEMORY = {
    enabled: true, // On by default - AI remembers viewers
};
export const DEFAULT_STREAMER_MEMORY = {
    enabled: true,
};
export const DEFAULT_ADAPTIVE_RESPONSE = {
    enabled: true,
    profile: 'balanced',
    learningRate: 0.12,
    explorationRate: 0.08,
};
export const DEFAULT_AI_GUARDRAILS = {
    strictMode: true,
    allowIdleVisionMemoryWrites: true,
    allowIdleVisionViewerMemoryWrites: false,
    maxToolCallsPerTurn: 3,
};
export const DEFAULT_USER_SETTINGS = {
    channelName: '',
    persona: DEFAULT_PERSONA,
    triggers: DEFAULT_TRIGGERS,
    audio: DEFAULT_AUDIO,
    streamlabs: DEFAULT_STREAMLABS,
    profanityFilter: DEFAULT_PROFANITY_FILTER,
    viewerMemory: DEFAULT_VIEWER_MEMORY,
    streamerMemory: DEFAULT_STREAMER_MEMORY,
    adaptiveResponse: DEFAULT_ADAPTIVE_RESPONSE,
    aiGuardrails: DEFAULT_AI_GUARDRAILS,
};
// Audio constants
export const AUDIO_INPUT_SAMPLE_RATE = 16000;
export const AUDIO_OUTPUT_SAMPLE_RATE = 24000;
export const AUDIO_CHANNELS = 1;
export const AUDIO_BIT_DEPTH = 16;
export const AUDIO_BYTES_PER_SAMPLE = AUDIO_BIT_DEPTH / 8;
export const AUDIO_CHUNK_MS = 20; // 20ms realtime input chunks
// Gemini model
export const GEMINI_MODEL = 'models/gemini-2.5-flash-native-audio-latest';
// WebSocket
export const WS_PING_INTERVAL_MS = 30000;
export const WS_RECONNECT_DELAY_MS = 3000;
export const WS_MAX_RECONNECT_ATTEMPTS = 10;
/**
 * Build voice accent description for demo based on accent ID
 */
export function buildDemoVoiceAccent(accentId) {
    switch (accentId) {
        case 'british':
            return 'modern British accent';
        case 'australian':
            return 'friendly Australian accent';
        case 'scottish':
            return 'warm modern Scottish accent';
        case 'southern':
            return 'smooth Southern accent';
        case 'soft-french':
            return 'gentle French-inflected accent';
        case 'streamer-hype':
            return 'high-energy hype caster accent';
        case 'neutral-us':
        default:
            return 'refined neutral American accent';
    }
}
//# sourceMappingURL=defaults.js.map